import React, { useEffect, useState } from "react";
import { get, put, postForm, del } from "../api/config";
import ProjectsCRUD from "./ProjectsCRUD";
import ImageUpload from "../components/ImageUpload";
import ProjectTimeline from "../components/ProjectTimeline";
import UpdatePasssword from "../components/UpdatePasssword";

// StatTile Component
function StatTile({ label, value, icon, trend, trendValue, description }) {
  return (
    <div className="group relative bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border border-gray-100 hover:scale-[1.02] overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-green-50/20 via-blue-50/10 to-purple-50/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      <div className="relative z-10">
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm font-medium text-gray-500 uppercase tracking-wide">
            {label}
          </div>
          <div className="p-3 bg-gradient-to-br from-green-100 to-green-50 rounded-xl group-hover:from-green-200 group-hover:to-green-100 transition-all duration-300 shadow-sm">
            {icon}
          </div>
        </div>

        <div className="mb-2">
          <div className="text-3xl font-bold text-gray-800 bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
            {value}
          </div>
          {description && (
            <div className="text-xs text-gray-500 mt-1">{description}</div>
          )}
        </div>

        {trend && (
          <div
            className={`flex items-center text-sm font-medium px-2 py-1 rounded-full ${
              trend === "up"
                ? "bg-green-100 text-green-700"
                : trend === "down"
                ? "bg-red-100 text-red-700"
                : "bg-blue-100 text-blue-700"
            }`}
          >
            <span className="mr-1 text-base">
              {trend === "up" ? "↗" : trend === "down" ? "↘" : "→"}
            </span>
            {trendValue}
          </div>
        )}
      </div>

      <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-green-400 via-blue-500 to-purple-500 opacity-0 group-hover:opacity-5 transition-opacity duration-500" />
    </div>
  );
}

// ProgressRing Component
function ProgressRing({ percentage, color = "green", size = 80 }) {
  const radius = 34;
  const circumference = 2 * Math.PI * radius;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  const colorClasses = {
    green: "text-green-500",
    blue: "text-blue-500",
    purple: "text-purple-500",
    orange: "text-orange-500",
  };

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg className="transform -rotate-90" width={size} height={size}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth="4"
          fill="transparent"
          className="text-gray-200"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth="4"
          fill="transparent"
          strokeDasharray={strokeDasharray}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          className={`${colorClasses[color]} transition-all duration-1000 ease-out`}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className={`text-sm font-bold ${colorClasses[color]}`}>
          {percentage}%
        </span>
      </div>
    </div>
  );
}

// LoadingSkeleton Component
function LoadingSkeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
      {[...Array(4)].map((_, index) => (
        <div
          key={index}
          className="rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 p-6 animate-pulse"
        >
          <div className="h-4 bg-gray-300 rounded w-1/2 mb-4"></div>
          <div className="h-8 bg-gray-300 rounded w-3/4 mb-2"></div>
          <div className="h-3 bg-gray-300 rounded w-1/2"></div>
        </div>
      ))}
    </div>
  );
}

// FundSourceCard Component
function FundSourceCard({ source, index }) {
  const colors = ["green", "blue", "purple", "orange", "red"];
  const color = colors[index % colors.length];

  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-semibold text-gray-900">{source.fund_source}</h4>
          <p className="text-sm text-gray-500">
            {source.count} transaction{source.count !== 1 ? "s" : ""}
          </p>
        </div>
        <div className={`text-lg font-bold text-${color}-600`}>
          {formatCurrency(source.total_amount)}
        </div>
      </div>
    </div>
  );
}

// UtilizationCard Component
function UtilizationCard({ project }) {
  const utilization = project.utilization_percentage || 0;
  const color =
    utilization === 0 ? "orange" : utilization < 50 ? "red" : "green";

  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-gray-900 truncate">
          {project.project_name}
        </h4>
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${
            color === "green"
              ? "bg-green-100 text-green-800"
              : color === "red"
              ? "bg-red-100 text-red-800"
              : "bg-orange-100 text-orange-800"
          }`}
        >
          {utilization}% Utilized
        </span>
      </div>

      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Allocated</span>
          <span className="font-medium">
            {formatCurrency(project.total_allocated)}
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Utilized</span>
          <span className="font-medium">
            {formatCurrency(project.total_utilized)}
          </span>
        </div>
      </div>

      <div className="mt-3">
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all duration-1000 ${
              color === "green"
                ? "bg-green-500"
                : color === "red"
                ? "bg-red-500"
                : "bg-orange-500"
            }`}
            style={{ width: `${utilization}%` }}
          ></div>
        </div>
      </div>
    </div>
  );
}

// FundCard Component
function FundCard({ fund, onEdit, onAllocate, onViewAllocations }) {
  const formattedDate = new Date(fund.received_date).toLocaleDateString('en-IN');
  
  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300 p-6">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4">
        <div>
          <h3 className="text-xl font-bold text-gray-900 mb-1">{fund.fund_source}</h3>
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <span>📅 Received: {formattedDate}</span>
            <span>💬 {fund.remarks || "No remarks"}</span>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => onEdit(fund)}
            className="px-4 py-2 bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-100 transition-colors font-medium flex items-center"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Edit
          </button>
          <button
            onClick={() => onAllocate(fund)}
            className="px-4 py-2 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-colors font-medium flex items-center"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Allocate
          </button>
          <button
            onClick={() => onViewAllocations(fund)}
            className="px-4 py-2 bg-purple-50 text-purple-600 rounded-xl hover:bg-purple-100 transition-colors font-medium flex items-center"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            View
          </button>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4">
          <div className="text-sm text-gray-600 mb-1">Total Amount</div>
          <div className="text-2xl font-bold text-gray-900">{formatCurrency(fund.amount)}</div>
        </div>
        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-4">
          <div className="text-sm text-gray-600 mb-1">Allocated</div>
          <div className="text-2xl font-bold text-gray-900">{formatCurrency(fund.total_allocated || 0)}</div>
        </div>
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4">
          <div className="text-sm text-gray-600 mb-1">Remaining</div>
          <div className="text-2xl font-bold text-gray-900">{formatCurrency(fund.remaining_amount || fund.amount)}</div>
        </div>
      </div>
      
      {fund.allocation_count > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="text-sm font-medium text-gray-700 mb-2">Allocations ({fund.allocation_count})</div>
          <div className="space-y-2">
            {fund.fund_management?.slice(0, 3).map((allocation, index) => (
              <div key={index} className="flex items-center justify-between text-sm p-2 bg-gray-50 rounded-lg">
                <div className="font-medium">{allocation.project?.project_name}</div>
                <div className="text-gray-600">{formatCurrency(allocation.allocated_amount)}</div>
              </div>
            ))}
            {fund.allocation_count > 3 && (
              <div className="text-center text-sm text-gray-500">
                +{fund.allocation_count - 3} more allocations
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// AllocationModal Component
function AllocationModal({ isOpen, onClose, fund, projects, onSubmit }) {
  const [formData, setFormData] = useState({
    project_id: "",
    allocated_amount: "",
    allocation_date: new Date().toISOString().split('T')[0],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (fund) {
      setFormData({
        project_id: "",
        allocated_amount: "",
        allocation_date: new Date().toISOString().split('T')[0],
      });
    }
  }, [fund]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.project_id || !formData.allocated_amount) {
      setError("Please fill in all required fields");
      return;
    }

    setLoading(true);
    setError("");

    const formDataToSend = new FormData();
    formDataToSend.append('project_id', formData.project_id);
    formDataToSend.append('fund_id', fund.fund_id);
    formDataToSend.append('allocated_amount', formData.allocated_amount);
    formDataToSend.append('allocation_date', formData.allocation_date);

    try {
      await postForm('fund-management', formDataToSend);
      onSubmit();
      onClose();
    } catch (err) {
      console.error("Allocation error details:", err);
      setError(err.message || "Failed to allocate funds. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const remainingAmount = parseFloat(fund?.remaining_amount || fund?.amount) - parseFloat(formData.allocated_amount || 0);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-2xl font-bold text-gray-900">Allocate Funds</h3>
              <p className="text-gray-600 mt-1">From {fund?.fund_source}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Project
              </label>
              <select
                value={formData.project_id}
                onChange={(e) => setFormData({...formData, project_id: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="">Choose a project</option>
                {projects?.map(project => (
                  <option key={project.project_id} value={project.project_id}>
                    {project.project_name} - {project.location} ({formatCurrency(project.estimated_cost)})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Amount to Allocate
              </label>
              <div className="relative">
                <span className="absolute left-4 top-3 text-gray-500">₹</span>
                <input
                  type="number"
                  value={formData.allocated_amount}
                  onChange={(e) => setFormData({...formData, allocated_amount: e.target.value})}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter amount"
                  required
                  min="0"
                  max={fund?.remaining_amount || fund?.amount}
                  step="0.01"
                />
              </div>
              <div className="mt-2 text-sm text-gray-500">
                Available: {formatCurrency(fund?.remaining_amount || fund?.amount)} | 
                Remaining after allocation: {formatCurrency(remainingAmount)}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Allocation Date
              </label>
              <input
                type="date"
                value={formData.allocation_date}
                onChange={(e) => setFormData({...formData, allocation_date: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div className="flex space-x-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl hover:opacity-90 transition-opacity font-medium disabled:opacity-50"
                disabled={loading}
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin h-5 w-5 mr-3 text-white" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Allocating...
                  </span>
                ) : "Allocate Funds"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// EditAllocationModal Component
function EditAllocationModal({ isOpen, onClose, allocation, onSubmit }) {
  const [formData, setFormData] = useState({
    allocated_amount: "",
    utilized_amount: "",
    allocation_date: ""
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (allocation) {
      setFormData({
        allocated_amount: allocation.allocated_amount || "",
        utilized_amount: allocation.utilized_amount || "",
        allocation_date: allocation.allocation_date ? allocation.allocation_date.split('T')[0] : ""
      });
    }
  }, [allocation]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.allocated_amount || !formData.allocation_date) {
      setError("Please fill in all required fields");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await put(`fund-management/${allocation.fund_mgmt_id}`, formData);
      onSubmit();
      onClose();
    } catch (err) {
      console.error("Edit allocation error:", err);
      setError(err.message || "Failed to update allocation. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-2xl font-bold text-gray-900">Edit Allocation</h3>
              <p className="text-gray-600 mt-1">
                {allocation?.project?.project_name} - {allocation?.fund?.fund_source}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Allocated Amount (₹) *
              </label>
              <input
                type="number"
                value={formData.allocated_amount}
                onChange={(e) => setFormData({...formData, allocated_amount: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter allocated amount"
                required
                min="0"
                step="0.01"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Utilized Amount (₹)
              </label>
              <input
                type="number"
                value={formData.utilized_amount}
                onChange={(e) => setFormData({...formData, utilized_amount: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter utilized amount"
                min="0"
                max={formData.allocated_amount}
                step="0.01"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Allocation Date *
              </label>
              <input
                type="date"
                value={formData.allocation_date}
                onChange={(e) => setFormData({...formData, allocation_date: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div className="flex space-x-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl hover:opacity-90 transition-opacity font-medium disabled:opacity-50"
                disabled={loading}
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin h-5 w-5 mr-3 text-white" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Updating...
                  </span>
                ) : "Update Allocation"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// FundManagementTable Component
function FundManagementTable({ allocations, loading, onEdit, onDelete, fundId = null }) {
  const filteredAllocations = fundId 
    ? allocations.filter(allocation => allocation.fund_id == fundId)
    : allocations;

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="bg-gray-100 rounded-xl p-6 animate-pulse">
            <div className="h-4 bg-gray-300 rounded w-1/4 mb-4"></div>
            <div className="h-6 bg-gray-300 rounded w-3/4"></div>
          </div>
        ))}
      </div>
    );
  }

  if (!filteredAllocations || filteredAllocations.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-400 mb-4">
          <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Allocations Found</h3>
        <p className="text-gray-500">Start by allocating funds to projects.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                Project
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                Fund Source
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                Allocated
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                Utilized
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                Date
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredAllocations.map((allocation) => {
              const utilizationPercent = parseFloat(allocation.utilized_amount) > 0 
                ? (parseFloat(allocation.utilized_amount) / parseFloat(allocation.allocated_amount)) * 100 
                : 0;
              
              return (
                <tr key={allocation.fund_mgmt_id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <div>
                      <div className="font-medium text-gray-900">{allocation.project?.project_name}</div>
                      <div className="text-sm text-gray-500">{allocation.project?.location}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-gray-900">{allocation.fund?.fund_source}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="font-semibold text-gray-900">{formatCurrency(allocation.allocated_amount)}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="font-semibold text-gray-900">{formatCurrency(allocation.utilized_amount)}</div>
                    <div className="text-sm text-gray-500">
                      {utilizationPercent.toFixed(1)}% utilized
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-gray-900">
                      {new Date(allocation.allocation_date).toLocaleDateString('en-IN')}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      utilizationPercent === 0 
                        ? "bg-yellow-100 text-yellow-800"
                        : utilizationPercent < 100
                        ? "bg-blue-100 text-blue-800"
                        : "bg-green-100 text-green-800"
                    }`}>
                      {utilizationPercent === 0 ? "Not Started" : 
                       utilizationPercent < 100 ? "In Progress" : "Completed"}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => onEdit(allocation)}
                        className="text-blue-600 hover:text-blue-800 transition-colors"
                        title="Edit"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => onDelete(allocation)}
                        className="text-red-600 hover:text-red-800 transition-colors"
                        title="Delete"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// EditFundModal Component
function EditFundModal({ isOpen, onClose, fund, onSubmit }) {
  const [formData, setFormData] = useState({
    fund_source: "",
    amount: "",
    received_date: "",
    remarks: ""
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (fund) {
      setFormData({
        fund_source: fund.fund_source || "",
        amount: fund.amount || "",
        received_date: fund.received_date ? fund.received_date.split('T')[0] : "",
        remarks: fund.remarks || ""
      });
    }
  }, [fund]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.fund_source || !formData.amount || !formData.received_date) {
      setError("Please fill in all required fields");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await put(`funds/${fund.fund_id}`, formData);
      onSubmit();
      onClose();
    } catch (err) {
      console.error("Edit fund error:", err);
      setError(err.message || "Failed to update fund. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-2xl font-bold text-gray-900">Edit Fund</h3>
              <p className="text-gray-600 mt-1">Update fund details</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fund Source *
              </label>
              <input
                type="text"
                value={formData.fund_source}
                onChange={(e) => setFormData({...formData, fund_source: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., MP Fund, State Grant, etc."
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Amount (₹) *
              </label>
              <input
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({...formData, amount: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter amount"
                required
                min="0"
                step="0.01"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Received Date *
              </label>
              <input
                type="date"
                value={formData.received_date}
                onChange={(e) => setFormData({...formData, received_date: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Remarks
              </label>
              <textarea
                value={formData.remarks}
                onChange={(e) => setFormData({...formData, remarks: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Any additional remarks"
                rows="3"
              />
            </div>

            <div className="flex space-x-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl hover:opacity-90 transition-opacity font-medium disabled:opacity-50"
                disabled={loading}
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin h-5 w-5 mr-3 text-white" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Updating...
                  </span>
                ) : "Update Fund"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// Main AdminDashboard Component
function AdminDashboard() {
  const [loading, setLoading] = useState({
    stats: true,
    funds: false,
    allocations: false,
    projects: false
  });
  const [error, setError] = useState("");
  const [stats, setStats] = useState(null);
  const [funds, setFunds] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showAllocationModal, setShowAllocationModal] = useState(false);
  const [showEditFundModal, setShowEditFundModal] = useState(false);
  const [showEditAllocationModal, setShowEditAllocationModal] = useState(false);
  const [showFundAllocationsModal, setShowFundAllocationsModal] = useState(false);
  const [selectedFund, setSelectedFund] = useState(null);
  const [selectedAllocation, setSelectedAllocation] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(prev => ({ ...prev, stats: true, funds: true, allocations: true, projects: true }));
    setError("");
    
    try {
      // Fetch statistics - Updated to use fund-management/statistics
      const statsRes = await get("fund-management/statistics");
      setStats(statsRes?.data || null);

      // Fetch funds
      const fundsRes = await get("funds");
      setFunds(fundsRes?.data || []);

      // Fetch allocations
      const allocationsRes = await get("fund-management");
      setAllocations(allocationsRes?.data || []);

      // Fetch projects for dropdown
      const projectsRes = await get("public/projects");
      setProjects(projectsRes?.data || []);

    } catch (e) {
      setError("Unable to load dashboard data.");
      console.error("Error fetching data:", e);
    } finally {
      setLoading(prev => ({ ...prev, stats: false, funds: false, allocations: false, projects: false }));
    }
  };

  const handleEditFund = (fund) => {
    setSelectedFund(fund);
    setShowEditFundModal(true);
  };

  const handleUpdateFund = async (updatedData) => {
    try {
      await put(`funds/${selectedFund.fund_id}`, updatedData);
      // Refresh data
      fetchDashboardData();
      setShowEditFundModal(false);
    } catch (e) {
      console.error("Error updating fund:", e);
    }
  };

  const handleAllocateFund = (fund) => {
    setSelectedFund(fund);
    setShowAllocationModal(true);
  };

  const handleViewFundAllocations = (fund) => {
    setSelectedFund(fund);
    setShowFundAllocationsModal(true);
  };

  const handleEditAllocation = (allocation) => {
    setSelectedAllocation(allocation);
    setShowEditAllocationModal(true);
  };

  const handleUpdateAllocation = async (updatedData) => {
    try {
      await put(`fund-management/${selectedAllocation.fund_mgmt_id}`, updatedData);
      // Refresh data
      fetchDashboardData();
      setShowEditAllocationModal(false);
    } catch (e) {
      console.error("Error updating allocation:", e);
    }
  };

  const handleDeleteAllocation = async (allocation) => {
    if (window.confirm("Are you sure you want to delete this allocation?")) {
      try {
        await del(`fund-management/${allocation.fund_mgmt_id}`);
        // Refresh data
        fetchDashboardData();
      } catch (e) {
        console.error("Error deleting allocation:", e);
        alert("Failed to delete allocation. Please try again.");
      }
    }
  };

  const handleAllocationSuccess = () => {
    fetchDashboardData(); // Refresh all data
  };

  const getUtilizationRate = () => {
    if (!stats?.total_allocated || parseFloat(stats.total_allocated) === 0) return 0;
    return (parseFloat(stats.total_utilized) / parseFloat(stats.total_allocated)) * 100;
  };

  const getAllocationRate = () => {
    if (!stats?.total_funds || parseFloat(stats.total_funds) === 0) return 0;
    return (parseFloat(stats.total_allocated) / parseFloat(stats.total_funds)) * 100;
  };

  // Stat Icons
  const statIcons = {
    total_funds: (
      <svg
        className="w-6 h-6 text-green-600"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
        />
      </svg>
    ),
    total_allocated: (
      <svg
        className="w-6 h-6 text-blue-600"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
        />
      </svg>
    ),
    total_utilized: (
      <svg
        className="w-6 h-6 text-purple-600"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
        />
      </svg>
    ),
    total_remaining: (
      <svg
        className="w-6 h-6 text-orange-600"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="mx-auto max-w-7xl px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-green-600 via-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                Fund Management Dashboard
              </h1>
              <p className="text-gray-600 text-lg">
                Comprehensive overview of funds, allocations, and project utilization
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-3 rounded-xl shadow-lg font-medium">
                Total Funds: {formatCurrency(stats?.total_funds || 0)}
              </div>
              <div className="flex items-center space-x-4 bg-white rounded-2xl px-6 py-3 shadow-sm border">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-blue-500 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-md">
                  A
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Admin User</p>
                  <p className="text-sm text-gray-500">Administrator</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="mb-8">
          <div className="flex space-x-2 overflow-x-auto pb-2">
            {["overview", "funds", "allocations", "projects", "projectTimeline", "uploadProjectMedia", "updatePassword"].map((tab) => {
              // Convert camelCase to Title Case
              const tabLabel = tab
                .replace(/([A-Z])/g, ' $1')
                .replace(/^./, str => str.toUpperCase())
                .trim();
              
              return (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-6 py-3 rounded-xl font-medium whitespace-nowrap transition-all ${
                    activeTab === tab
                      ? "bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg"
                      : "bg-white text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  {tabLabel}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content based on active tab */}
        {activeTab === "overview" && (
          <>
            {/* Stats Grid */}
            {loading.stats ? (
              <LoadingSkeleton />
            ) : error ? (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-8 text-center shadow-sm">
                <div className="text-red-600 text-xl font-semibold mb-3">
                  Unable to Load Data
                </div>
                <p className="text-red-500 mb-6">{error}</p>
                <button
                  onClick={fetchDashboardData}
                  className="bg-red-600 text-white px-8 py-3 rounded-xl hover:bg-red-700 transition-colors shadow-md font-medium"
                >
                  Try Again
                </button>
              </div>
            ) : stats ? (
              <>
                {/* Main Statistics */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                  <StatTile
                    label="Total Funds"
                    value={formatCurrency(stats.total_funds)}
                    icon={statIcons.total_funds}
                    trend="stable"
                    trendValue="Available"
                  />
                  <StatTile
                    label="Total Allocated"
                    value={formatCurrency(stats.total_allocated)}
                    icon={statIcons.total_allocated}
                    trend="up"
                    trendValue={`${getAllocationRate().toFixed(1)}% of total`}
                    description={`${stats.allocation_count} allocation${
                      stats.allocation_count !== 1 ? "s" : ""
                    }`}
                  />
                  <StatTile
                    label="Total Utilized"
                    value={formatCurrency(stats.total_utilized)}
                    icon={statIcons.total_utilized}
                    trend="up"
                    trendValue={`${getUtilizationRate().toFixed(1)}% of allocated`}
                  />
                  <StatTile
                    label="Remaining Funds"
                    value={formatCurrency(stats.total_remaining)}
                    icon={statIcons.total_remaining}
                    trend="stable"
                    trendValue="Available"
                  />
                </div>

                {/* Detailed Analytics Section */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
                  {/* Allocation Progress */}
                  <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 lg:col-span-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-6">
                      Fund Allocation
                    </h3>
                    <div className="flex flex-col items-center">
                      <ProgressRing
                        percentage={getAllocationRate()}
                        color="blue"
                        size={120}
                      />
                      <div className="mt-4 text-center">
                        <div className="text-2xl font-bold text-gray-900">
                          {getAllocationRate().toFixed(1)}%
                        </div>
                        <div className="text-sm text-gray-500">
                          of total funds allocated
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Utilization Progress */}
                  <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 lg:col-span-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-6">
                      Fund Utilization
                    </h3>
                    <div className="flex flex-col items-center">
                      <ProgressRing
                        percentage={getUtilizationRate()}
                        color="green"
                        size={120}
                      />
                      <div className="mt-4 text-center">
                        <div className="text-2xl font-bold text-gray-900">
                          {getUtilizationRate().toFixed(1)}%
                        </div>
                        <div className="text-sm text-gray-500">
                          of allocated funds utilized
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Quick Stats */}
                  <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 lg:col-span-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-6">
                      Quick Stats
                    </h3>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center p-3 bg-green-50 rounded-xl">
                        <span className="text-green-700 font-medium">
                          Allocations
                        </span>
                        <span className="text-green-900 font-bold text-lg">
                          {stats.allocation_count}
                        </span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-blue-50 rounded-xl">
                        <span className="text-blue-700 font-medium">
                          Fund Sources
                        </span>
                        <span className="text-blue-900 font-bold text-lg">
                          {stats.fund_sources?.length || 0}
                        </span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-purple-50 rounded-xl">
                        <span className="text-purple-700 font-medium">
                          Active Projects
                        </span>
                        <span className="text-purple-900 font-bold text-lg">
                          {stats.utilization_by_project?.length || 0}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg p-6 border border-gray-100">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
                    <div className="space-y-3">
                      {/* <button
                        onClick={() => setActiveTab("funds")}
                        className="w-full px-4 py-3 bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-100 transition-colors font-medium flex items-center justify-center"
                      >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Add New Fund
                      </button> */}
                      <button
                        onClick={() => {
                          if (funds.length > 0) {
                            handleAllocateFund(funds[0]);
                          }
                        }}
                        className="w-full px-4 py-3 bg-green-50 text-green-600 rounded-xl hover:bg-green-100 transition-colors font-medium flex items-center justify-center"
                      >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Allocate Funds
                      </button>
                    </div>
                  </div>

                  {/* Recent Allocations */}
                  <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 md:col-span-2">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Allocations</h3>
                    <div className="space-y-4">
                      {allocations.slice(0, 3).map((allocation) => (
                        <div key={allocation.fund_mgmt_id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                          <div>
                            <div className="font-medium text-gray-900">{allocation.project?.project_name}</div>
                            <div className="text-sm text-gray-500">{allocation.fund?.fund_source}</div>
                          </div>
                          <div className="text-right">
                            <div className="font-bold text-gray-900">{formatCurrency(allocation.allocated_amount)}</div>
                            <div className="text-sm text-gray-500">
                              {new Date(allocation.allocation_date).toLocaleDateString('en-IN')}
                            </div>
                          </div>
                        </div>
                      ))}
                      {allocations.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                          No allocations yet
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Fund Sources & Project Utilization */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                  {/* Fund Sources */}
                  <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Fund Sources
                    </h3>
                    <div className="space-y-3">
                      {stats.fund_sources?.map((source, index) => (
                        <FundSourceCard key={index} source={source} index={index} />
                      ))}
                      {(!stats.fund_sources || stats.fund_sources.length === 0) && (
                        <div className="text-center py-8 text-gray-500">
                          No fund sources data available
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Project Utilization */}
                  <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Project Utilization
                    </h3>
                    <div className="space-y-4">
                      {stats.utilization_by_project?.map((project, index) => (
                        <UtilizationCard key={index} project={project} />
                      ))}
                      {(!stats.utilization_by_project ||
                        stats.utilization_by_project.length === 0) && (
                        <div className="text-center py-8 text-gray-500">
                          No project utilization data available
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            ) : null}
          </>
        )}

        {activeTab === "funds" && (
          <div className="space-y-6">
            {/* <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Fund Sources</h2>
              <button
                onClick={() => {
                  // TODO: Implement add new fund functionality
                  console.log("Add new fund");
                }}
                className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl hover:opacity-90 transition-opacity font-medium flex items-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add New Fund
              </button>
            </div> */}

            {loading.funds ? (
              <div className="space-y-4">
                {[...Array(2)].map((_, i) => (
                  <div key={i} className="bg-gray-100 rounded-2xl p-6 animate-pulse">
                    <div className="h-6 bg-gray-300 rounded w-1/3 mb-4"></div>
                    <div className="h-4 bg-gray-300 rounded w-1/2"></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-6">
                {funds.map((fund) => (
                  <FundCard
                    key={fund.fund_id}
                    fund={fund}
                    onEdit={() => handleEditFund(fund)}
                    onAllocate={() => handleAllocateFund(fund)}
                    onViewAllocations={() => handleViewFundAllocations(fund)}
                  />
                ))}
                {funds.length === 0 && (
                  <div className="text-center py-12 bg-white rounded-2xl shadow-lg">
                    <div className="text-gray-400 mb-4">
                      <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Funds Added</h3>
                    <p className="text-gray-500 mb-6">Start by adding your first fund source.</p>
                    <button className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl hover:opacity-90 transition-opacity font-medium">
                      Add First Fund
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === "allocations" && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Fund Allocations</h2>
              <div className="text-sm text-gray-500">
                {allocations.length} allocation{allocations.length !== 1 ? 's' : ''} found
              </div>
            </div>

            <FundManagementTable 
              allocations={allocations} 
              loading={loading.allocations}
              onEdit={handleEditAllocation}
              onDelete={handleDeleteAllocation}
            />

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl p-6">
                <div className="text-sm text-gray-600 mb-2">Total Allocated</div>
                <div className="text-3xl font-bold text-gray-900">
                  {formatCurrency(
                    allocations.reduce((sum, a) => sum + parseFloat(a.allocated_amount), 0)
                  )}
                </div>
              </div>
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6">
                <div className="text-sm text-gray-600 mb-2">Total Utilized</div>
                <div className="text-3xl font-bold text-gray-900">
                  {formatCurrency(
                    allocations.reduce((sum, a) => sum + parseFloat(a.utilized_amount), 0)
                  )}
                </div>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6">
                <div className="text-sm text-gray-600 mb-2">Average Utilization</div>
                <div className="text-3xl font-bold text-gray-900">
                  {allocations.length > 0
                    ? `${(
                        allocations.reduce((sum, a) => {
                          const utilized = parseFloat(a.utilized_amount);
                          const allocated = parseFloat(a.allocated_amount);
                          return allocated > 0 ? sum + (utilized / allocated) * 100 : sum;
                        }, 0) / allocations.length
                      ).toFixed(1)}%`
                    : "0%"}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "projects" && (
          <ProjectsCRUD />
        )}

        {activeTab === "projectTimeline" && (
          <ProjectTimeline />
        )}

        {activeTab === "uploadProjectMedia" && (
          <ImageUpload />
        )}

        {activeTab === "updatePassword" && (
          <UpdatePasssword />
        )}

        {/* Allocation Modal */}
        <AllocationModal
          isOpen={showAllocationModal}
          onClose={() => setShowAllocationModal(false)}
          fund={selectedFund}
          projects={projects}
          onSubmit={handleAllocationSuccess}
        />

        {/* Edit Fund Modal */}
        <EditFundModal
          isOpen={showEditFundModal}
          onClose={() => setShowEditFundModal(false)}
          fund={selectedFund}
          onSubmit={handleUpdateFund}
        />

        {/* Edit Allocation Modal */}
        <EditAllocationModal
          isOpen={showEditAllocationModal}
          onClose={() => setShowEditAllocationModal(false)}
          allocation={selectedAllocation}
          onSubmit={handleUpdateAllocation}
        />

        {/* Fund Allocations Modal */}
        {showFundAllocationsModal && selectedFund && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">Allocations for {selectedFund.fund_source}</h3>
                    <p className="text-gray-600 mt-1">
                      Total: {formatCurrency(selectedFund.amount)} | 
                      Allocated: {formatCurrency(selectedFund.total_allocated || 0)} | 
                      Remaining: {formatCurrency(selectedFund.remaining_amount || selectedFund.amount)}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowFundAllocationsModal(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <FundManagementTable 
                  allocations={allocations} 
                  loading={loading.allocations}
                  onEdit={handleEditAllocation}
                  onDelete={handleDeleteAllocation}
                  fundId={selectedFund.fund_id}
                />
              </div>
            </div>
          </div>
        )}

        {/* Other Sections */}
        {/* <div className="space-y-8 mt-8">
          {activeTab === "overview" && <ProjectTimeline />}
          <ImageUpload />
        </div> */}
      </div>
    </div>
  );
}

// Helper function for currency formatting
function formatCurrency(amount) {
  if (!amount && amount !== 0) return "₹ 0";
  const numAmount = parseFloat(amount);

  if (numAmount >= 10000000) {
    return `₹ ${(numAmount / 10000000).toFixed(1)} Cr`;
  } else if (numAmount >= 100000) {
    return `₹ ${(numAmount / 100000).toFixed(1)} L`;
  } else if (numAmount >= 1000) {
    return `₹ ${(numAmount / 1000).toFixed(1)} K`;
  }

  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(numAmount);
}

export default AdminDashboard;